import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import { POPOVER_RIGHT_START } from "designSystem/styles/popover-positions";
import FlexTopDrawer from "designSystem/components/flex/FlexTopDrawer";
import { Box, FlexRowCenter, FormControlLabel, Grid, IconButton, OverflowText, Popover, Radio, RadioGroup } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { OverflowTextProps } from "designSystem/components/text/OverflowText";
import { TooltipProps } from "designSystem/components/tooltip/Tooltip";
import { getRuleSpecValueString } from "csi/middleware/attributeRule/RuleService";
import AttributeSettingStore, { AttributeSettingStoreContext } from "csi/middleware/AttributeSettingStore";
import FilterGroup, { GroupTitle } from "csi/middleware/filter/FilterGroup";
import csiPrefs from "csi/prefs/_base";
import { useStore } from "csi/state/hooks";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, MouseEventHandler, useState } from "react";
import { PPWizardStoreContext } from "./NewProductPresentation/PPWizardStore";
import PPEditRuleDialog from "./PPEditRuleDialog";
import { usePPProductLayoutStore } from "./PPProductLayoutStore";
import { isPresentationEditor } from "./PPService";

const PREFIX = "PPFilterPane";

const classes = {
	root: `${PREFIX}root`,
	section: `${PREFIX}section`,
	sectionTitle: `${PREFIX}sectionTitle`,
};

const StyledFlexTopDrawer = styled(FlexTopDrawer)((
	{
		theme,
	},
) => ({
	[`&.${classes.root}`]: {
		borderRight: STANDARD_BORDER_STYLE,
		flexShrink: 0,
		overflow: "auto",
		paddingLeft: theme.spacing(2),
		width: 256,
	},

	[`& .${classes.section}`]: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(4),
	},

	[`& .${classes.sectionTitle}`]: {
		justifyContent: "space-between",
		marginRight: theme.spacing(2),
	},
}));

const tooltipProps: Partial<TooltipProps> = { placement: "right" };

const SectionTitle = (props: OverflowTextProps) => {
	return (
		<OverflowText
			bold
			color="textPrimary"
			size={14}
			tooltipProps={tooltipProps}
			{...props}
		/>
	);
};

const PPEditRule = observer(() => {
	// NOTE: do not allow rules to be changed while editing a set - this could affect validation of products and colors in the set
	const {
		editSetStore,
		ruleDialogStore,
		initialRuleDialogStore,
	} = usePPProductLayoutStore();

	const [isOpen, setOpen] = useState<boolean>(false);

	// handler for edit rule icon
	const handleEditRuleClick: MouseEventHandler = (event) => {
		setOpen(true);
		initialRuleDialogStore();
	};

	const onCloseEditRuleDialog = () => setOpen(false);

	return (
		<Fragment>
			<IconButton
				design="default"
				disabled={!!editSetStore}
				onClick={handleEditRuleClick}
				size="small"
			>
				<EditIcon />
			</IconButton>
			<PPWizardStoreContext.Provider value={ruleDialogStore}>
				<PPEditRuleDialog onClose={onCloseEditRuleDialog} open={isOpen} />
			</PPWizardStoreContext.Provider>
		</Fragment>
	);
});

const QuickFilterSection = observer(() => {
	const { filterStore } = usePPProductLayoutStore();
	const { quickFilterId, quickFilterResources, quickFilterOnChange } = filterStore || {};

	return (
		<>
			<Box mb={2}>
				<GroupTitle res="RuleQuickFilterSection" />
			</Box>
			<RadioGroup
				onChange={(event, value) => quickFilterOnChange(value)}
			>
				{quickFilterResources?.map((resource, i) => (
					<FormControlLabel
						checked={resource === quickFilterId}
						control={<Radio />}
						key={i}
						labelPlacement="end"
						res={resource}
						value={resource}
					/>
				),
				)}
			</RadioGroup>
		</>
	);
});

interface PPFilterPaneProps {
	/** component for attribute settings */
	SettingComponent?: FunctionComponent;

	/** store for setting component */
	settingComponentStore?: AttributeSettingStore;
}

const PPFilterPane: FunctionComponent<PPFilterPaneProps> = observer((props) => {
	const {
		filterStore,
		initialOpen,
		ruleSpecs,
		selectionStore,
	} = usePPProductLayoutStore();
	const {
		root,
		section,
		sectionTitle,
	} = classes;

	const { SettingComponent, settingComponentStore } = props;
	const { closePopup } = settingComponentStore || {};

	const [anchorEl, setAnchorEl] = useState(null);

	const { localStore } = useStore();
	const loc = useLocale();

	const { productPresentation } = localStore;
	const { filterTopOpen, setFilterTopOpen } = productPresentation;

	/** open up the pop up */
	const handleClick: MouseEventHandler = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const open = !!anchorEl;

	/** save the list when close the popup */
	const handleClose = () => {
		setAnchorEl(null);
		closePopup();
	};

	const top = (
		<Box className={section}>
			<FlexRowCenter className={sectionTitle}>
				<SectionTitle res="Rules" />
				{!selectionStore && isPresentationEditor() &&
					<PPEditRule />
				}
			</FlexRowCenter>
			<Box pb={4} pt={3}>
				{ruleSpecs.map((ruleSpec) => (
					<Grid
						container
						key={ruleSpec.spec.key.toString()}
						spacing={2}
					>
						<Grid item xs={5}>
							<OverflowText
								color="textSecondary"
								res="RuleNameOp"
								resParams={[ruleSpec.spec.name, loc.resource(`OP_${ruleSpec.rule.op}`)]}
								semiBold
							/>
						</Grid>
						<Grid item xs={7}>
							<OverflowText
								color="textPrimary"
								text={getRuleSpecValueString(ruleSpec)}
							/>
						</Grid>
					</Grid>
				))}
			</Box>
		</Box>
	);

	const topWhenClosed = (
		<FlexRowCenter className={`${section} ${sectionTitle}`}>
			<SectionTitle res="Rules" />
			{!selectionStore && isPresentationEditor() &&
				<PPEditRule />
			}
		</FlexRowCenter>
	);

	const bottom = (
		<Box className={section}>
			<FlexRowCenter className={sectionTitle}>
				<SectionTitle res="RuleFilters" />
				{csiPrefs.hasPersonalAccess() && (
					<IconButton
						design="default"
						onClick={handleClick}
						size="small"
					>
						<EditIcon />
					</IconButton>
				)}
			</FlexRowCenter>
			<Popover
				{...POPOVER_RIGHT_START}
				anchorEl={anchorEl}
				onClose={handleClose}
				open={open}
			>
				<AttributeSettingStoreContext.Provider value={settingComponentStore}>
					<SettingComponent />
				</AttributeSettingStoreContext.Provider>
			</Popover>
			<Box pt={3}>
				{!selectionStore && (
					<Box my={4}>
						<QuickFilterSection />
					</Box>
				)}
				<FilterGroup
					store={filterStore}
				/>
			</Box>
		</Box>
	);

	return (
		<StyledFlexTopDrawer
			className={root}
			heightClosed={48}
			onToggleOpen={(open) => setFilterTopOpen(open)}
			open={filterTopOpen === undefined ? initialOpen : filterTopOpen}
		>
			{{
				top,
				topWhenClosed,
				bottom,
			}}
		</StyledFlexTopDrawer>
	);
});

export default PPFilterPane;